import styled, { css, keyframes } from "styled-components";
import { colors, font, radius, shadow } from "../../../styles/theme/tokens";

const rise = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const text = css`
  font-family: ${font.text};
  color: ${colors.ink};
`;

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  max-width: 1040px;
  animation: ${rise} 0.35s ease-out;
`;

// ---------- Гаманець ----------

export const Wallet = styled.section`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 24px;
  padding: 28px;
  border-radius: ${radius.xl};
  color: #FFFFFF;
  background:
    radial-gradient(120% 140% at 100% 0%, rgba(244, 182, 63, 0.35) 0%, rgba(244, 182, 63, 0) 45%),
    linear-gradient(135deg, ${colors.brandDark} 0%, ${colors.brandDeep} 65%, ${colors.brand} 100%);
  box-shadow: ${shadow.raised};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 22px;
  }
`;

export const WalletMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const WalletLabel = styled.span`
  font-family: ${font.text};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  opacity: 0.8;
`;

export const WalletBalance = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${font.text};
  font-size: 56px;
  font-weight: 800;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
`;

export const Goal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
  max-width: 420px;
`;

export const GoalText = styled.p`
  font-family: ${font.text};
  font-size: 14px;
  font-weight: 500;
  opacity: 0.92;

  b {
    font-weight: 800;
  }
`;

export const Progress = styled.div`
  height: 8px;
  border-radius: ${radius.pill};
  overflow: hidden;
  background: ${(props) => (props.$dark ? "rgba(255, 255, 255, 0.18)" : colors.brandSoft)};
`;

export const ProgressFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #FFD66B 0%, ${colors.amber} 100%);
  transition: width 0.6s ease-out;
`;

export const Earn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  border-radius: ${radius.lg};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
`;

export const EarnTitle = styled.span`
  font-family: ${font.text};
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.75;
`;

export const EarnRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const EarnIcon = styled.span`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: ${radius.sm};
  background: rgba(255, 255, 255, 0.16);
`;

export const EarnText = styled.span`
  flex: 1;
  font-family: ${font.text};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
`;

export const EarnAmount = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: ${font.text};
  font-size: 15px;
  font-weight: 800;
  white-space: nowrap;
`;

// ---------- Повідомлення ----------

export const Notice = styled.p`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${font.text};
  font-size: 15px;
  font-weight: 600;
  padding: 14px 18px;
  border-radius: ${radius.md};
  cursor: pointer;
  animation: ${rise} 0.25s ease-out;
  background: ${(props) => (props.$type === "error" ? colors.dangerSoft : colors.successSoft)};
  color: ${(props) => (props.$type === "error" ? colors.danger : colors.success)};
`;

// ---------- Товари ----------

export const SectionTitle = styled.h2`
  ${text};
  font-size: 22px;
  font-weight: 800;
  margin-bottom: -12px;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
`;

export const Card = styled.article`
  display: flex;
  flex-direction: column;
  border-radius: ${radius.lg};
  overflow: hidden;
  background: ${colors.surface};
  border: 1px solid ${colors.line};
  box-shadow: ${shadow.card};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${shadow.raised};
  }
`;

export const CardArt = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  height: 120px;
  background: ${(props) => props.$tint};
  color: ${(props) => props.$color};
`;

export const PricePill = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: ${radius.pill};
  background: ${colors.surface};
  box-shadow: 0 2px 8px rgba(30, 27, 58, 0.08);
  ${text};
  font-size: 15px;
  font-weight: 800;
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 18px 0;
  flex-grow: 1;
`;

export const CardTitle = styled.h3`
  ${text};
  font-size: 17px;
  font-weight: 800;
  line-height: 1.3;
`;

export const CardText = styled.p`
  font-family: ${font.text};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: ${colors.muted};
`;

export const CardFooter = styled.div`
  padding: 16px 18px 18px;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  font-family: ${font.text};
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.08s ease, box-shadow 0.08s ease, opacity 0.2s;

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase};
  padding: 13px 20px;
  border-radius: ${radius.md};
  color: #FFFFFF;
  background: ${colors.brand};
  box-shadow: ${shadow.button(colors.brandDeep)};

  &:hover:not(:disabled) {
    background: #8683EE;
  }
  &:active:not(:disabled) {
    transform: translateY(3px);
    box-shadow: ${shadow.button("transparent")};
  }
`;

export const TextButton = styled.button`
  ${buttonBase};
  padding: 10px;
  font-size: 15px;
  font-weight: 700;
  color: ${colors.muted};
  background: none;
`;

export const Locked = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const LockedText = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: ${font.text};
  font-size: 13px;
  font-weight: 700;
  color: ${colors.muted};
`;

export const Done = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-radius: ${radius.md};
  font-family: ${font.text};
  font-size: 15px;
  font-weight: 700;
  color: ${colors.success};
  background: ${colors.successSoft};
`;

// ---------- Замовлення ----------

export const Orders = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Order = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: ${radius.md};
  background: ${colors.surface};
  border: 1px solid ${colors.line};

  @media (max-width: 560px) {
    flex-wrap: wrap;
  }
`;

export const OrderIcon = styled.span`
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: ${radius.sm};
  background: ${(props) => props.$tint};
  color: ${(props) => props.$color};
`;

export const OrderMain = styled.div`
  flex: 1;
  min-width: 0;
`;

export const OrderTitle = styled.p`
  ${text};
  font-size: 15px;
  font-weight: 700;
`;

export const OrderMeta = styled.p`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 3px;
  font-family: ${font.text};
  font-size: 13px;
  font-weight: 500;
  color: ${colors.muted};
`;

const STATUS_COLORS = {
  new: [colors.amberSoft, colors.amberDeep],
  done: [colors.successSoft, colors.success],
  cancelled: ["#F1F0F6", colors.muted],
};

export const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: ${radius.pill};
  font-family: ${font.text};
  font-size: 13px;
  font-weight: 700;
  background: ${(props) => STATUS_COLORS[props.$status]?.[0]};
  color: ${(props) => STATUS_COLORS[props.$status]?.[1]};
`;

// ---------- Модальні вікна ----------

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(30, 27, 58, 0.45);
  backdrop-filter: blur(3px);
  animation: ${fadeIn} 0.2s ease-out;
`;

export const Modal = styled.div`
  position: relative;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 24px 20px;
  border-radius: ${radius.xl};
  text-align: center;
  background: ${colors.surface};
  box-shadow: ${shadow.raised};
  animation: ${popIn} 0.25s ease-out;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: ${radius.pill};
  color: ${colors.muted};
  background: #F3F2FA;
  cursor: pointer;
`;

export const ModalIcon = styled.span`
  display: grid;
  place-items: center;
  width: 68px;
  height: 68px;
  border-radius: ${radius.lg};
  background: ${(props) => props.$tint};
  color: ${(props) => props.$color};
`;

export const ModalTitle = styled.h3`
  ${text};
  font-size: 20px;
  font-weight: 800;
  line-height: 1.3;
`;

export const ModalText = styled.p`
  font-family: ${font.text};
  font-size: 15px;
  font-weight: 500;
  line-height: 1.55;
  color: ${colors.muted};
  margin-bottom: 6px;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const Input = styled.input`
  font-family: ${font.text};
  font-size: 16px;
  font-weight: 500;
  color: ${colors.ink};
  padding: 13px 14px;
  border-radius: ${radius.md};
  border: 1.5px solid ${colors.line};
  background: ${colors.bg};
  outline: none;
  transition: border-color 0.15s, background 0.15s;

  &:focus {
    border-color: ${colors.brand};
    background: ${colors.surface};
  }
`;

export const FormError = styled.p`
  font-family: ${font.text};
  font-size: 14px;
  font-weight: 600;
  color: ${colors.danger};
  text-align: left;
`;
